import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import Quill styles
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from '@mui/material';

const BulkEmailView = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingSend, setLoadingSend] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch users on load
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const jwt = localStorage.getItem('jwttoken');
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/adminuserdata/all-users`,
          {
            method: 'GET',
            headers: { Authorization: `Bearer ${jwt}` },
          }
        );
        if (!response.ok) throw new Error(`Error fetching users: ${response.statusText}`);
        const json = await response.json();
        setUsers(json.data || []);
        setFilteredUsers(json.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  const handleSelectAllChange = (checked) => {
    setSelectAll(checked);
    setSelectedUsers(checked ? filteredUsers.map((user) => user.email) : []);
  };

  const handleCheckboxChange = (email, checked) => {
    setSelectedUsers((prev) =>
      checked ? [...prev, email] : prev.filter((user) => user !== email)
    );
    if (!checked) setSelectAll(false); // Uncheck "Select All" if any user is deselected
  };

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = users.filter(
      (user) =>
        user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query)
    );
    setFilteredUsers(filtered);

    // Update "Select All" if all visible users are selected
    setSelectAll(filtered.every((user) => selectedUsers.includes(user.email)));
  };

  const handleSendEmails = async () => {
    setLoadingSend(true);
    setError(null);
    setSuccess(null);
    try {
      const jwt = localStorage.getItem('jwttoken');

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/adminnotification/send-bulk-email`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            emails: selectedUsers,
            subject: emailSubject,
            body: emailBody, // Quill already returns formatted HTML
          }),
        }
      );
      if (!response.ok) throw new Error('Failed to send emails.');
      setSuccess('Emails sent successfully!');
      setEmailSubject('');
      setEmailBody('');
      setSelectedUsers([]);
      setSelectAll(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSend(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Send Bulk Email
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Box sx={{ mb: 3 }}>
        <TextField
          label="Subject"
          fullWidth
          value={emailSubject}
          onChange={(e) => setEmailSubject(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Rich Text Editor */}
        <Typography variant="h6" sx={{ mb: 1 }}>Email Body</Typography>
        <ReactQuill
          value={emailBody}
          onChange={setEmailBody}
          modules={{
            toolbar: [
              [{ header: [1, 2, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['link'],
              ['clean'],
            ],
          }}
          style={{ height: '200px', marginBottom: '20px' }}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleSendEmails}
          disabled={selectedUsers.length === 0 || loadingSend}
        >
          {loadingSend ? <CircularProgress size={20} /> : 'Send Emails'}
        </Button>
      </Box>

      <Typography variant="h5" sx={{ mb: 2 }}>
        Select Users
      </Typography>
      <TextField
        label="Search Users"
        placeholder="Search by name or email"
        fullWidth
        value={searchQuery}
        onChange={handleSearchChange}
        sx={{ mb: 3 }}
      />

      {loadingUsers ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Checkbox
                    checked={selectAll}
                    onChange={(e) => handleSelectAllChange(e.target.checked)}
                  />
                  Select All
                </TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedUsers.includes(user.email)}
                      onChange={(e) => handleCheckboxChange(user.email, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>{user.id}</TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default BulkEmailView;
